import { siteApi } from "@api";
import { closeDeposit } from "@components/utils/DepositDrawer/utils";
import { closeIframeDialog } from "@components/utils/IframeDialog";
import { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import { fAmount, FundingStatus, FundingTxModel, FundingType, GetFundingResponse } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import useAuthSession from "./useAuthSession";
import usePreference from "./usePreference";
import useProfileBalance from "./useProfileBalance";

interface UseDepositStatusWatcherProps {
  onUpdate?: (fundingTx: FundingTxModel | null) => void;
  onCompleted?: (fundingTx: FundingTxModel) => void;
  pollingInterval?: number;
}

/**
 * WS Reconnect interval = 5000
 * Polling interval = 6000
 * After user back to the site, ws reconnecting should be run first
 * In case of ws reconnecting failed, then fallback to polling
 */
const POLLING_INTERVAL = 6000;

/**
 * Interval check deposit status from redux store
 * This fallback to polling if the websocket is not connected
 */
export const useDepositStatusWatcher = (options?: UseDepositStatusWatcherProps) => {
  const { onUpdate, onCompleted, pollingInterval = POLLING_INTERVAL } = options || {};
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const { refetch: refetchBalance } = useProfileBalance();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthSession();

  const { pendingDepositOrderRef: orderRef, isWsConnected } = usePreference();
  const dispatch = useDispatch<AppDispatch>();
  const [fundingResponse, setFundingResponse] = useState<GetFundingResponse>();

  // cache requestedAtQuery to trigger polling twice
  const requestedAtQuery = useMemo(() => {
    if (!orderRef) {
      return null;
    }
    const last24h = Math.floor(moment().subtract(24, "hours").valueOf() / 1000);
    return `${last24h},`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderRef]);

  const handleOnCompleted = useCallback(
    (fundingTx: FundingTxModel) => {
      onCompleted?.(fundingTx);
      dispatch(preferenceSlice.actions.setPendingDepositOrderRef(null));

      toast.success(t("Deposit amount completed", { amount: fAmount(fundingTx?.netAmount) }), {
        id: `deposit-toast-${fundingTx?.orderRef}`,
      });

      closeIframeDialog({
        id: `deposit-${fundingTx?.orderRef}`,
      });
      closeDeposit();
      setTimeout(() => {
        // BE needs time to update balance
        refetchBalance();
      }, 5000);
    },
    [onCompleted, dispatch, refetchBalance, t],
  );

  const handleOnUpdate = useCallback(
    (res?: GetFundingResponse) => {
      if (res?.entries?.[0]) {
        onUpdate?.(res.entries[0]);
        if (res.entries[0].status === FundingStatus.Completed) {
          handleOnCompleted?.(res.entries[0]);
        }
      }
    },
    [onUpdate, handleOnCompleted],
  );

  // fallback to polling to fetch funding status when websocket is not connected
  const { data: pollingFundingRes, isFetching: isFetchingFundingRes } = siteApi.useGetFundingHistoryQuery(
    buildFundingHistoryQuery(orderRef!, requestedAtQuery),
    {
      pollingInterval,
      skip: !orderRef || isWsConnected || !isAuthenticated,
    },
  );

  // manually refetch funding status
  const [refetchFundingStatus] = siteApi.useLazyGetFundingHistoryQuery();

  useEffect(() => {
    if (shouldRefetch && !!orderRef && !isFetchingFundingRes && isAuthenticated) {
      refetchFundingStatus(buildFundingHistoryQuery(orderRef, requestedAtQuery))
        .then((res) => {
          setFundingResponse(res.data);
        })
        .finally(() => {
          setShouldRefetch(false);
        });
    }
  }, [
    shouldRefetch,
    orderRef,
    isFetchingFundingRes,
    handleOnCompleted,
    onUpdate,
    refetchBalance,
    refetchFundingStatus,
    requestedAtQuery,
    isAuthenticated,
  ]);

  // Handle funding detail response
  useEffect(() => {
    setFundingResponse(pollingFundingRes);
  }, [pollingFundingRes]);

  // trigger manual refetch when user returns to the site
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !!orderRef) {
        setShouldRefetch(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [orderRef, isWsConnected]);

  // trigger manual refetch when ws is reconnected
  useEffect(() => {
    if (isWsConnected && !!orderRef) {
      setShouldRefetch(true);
    }
  }, [isWsConnected, orderRef]);

  // Reset shouldRefetch when orderRef changes
  useEffect(() => {
    setShouldRefetch(false);
  }, [orderRef]);

  // Handle funding detail response
  useEffect(() => {
    if (fundingResponse) {
      handleOnUpdate(fundingResponse);
    }
  }, [fundingResponse, handleOnUpdate]);
};

const buildFundingHistoryQuery = (orderRef: string, timeStampParams: string | null) => {
  return {
    query: {
      limit: 1,
      offset: 0,
      orderRef: orderRef,
      type: FundingType.Deposit,
      requestedAt: timeStampParams,
    },
  };
};
